'use client';

import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';

import { Button } from '@/components/button/button';
import Modal from '@/components/modal/modal';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useDeleteInvitationMutation } from '@/hooks/queries/useOrg';
import { OrgInvitation } from '@/types/auth';


interface InvitationsTableProps {
  invitations: OrgInvitation[];
}

export default function InvitationsTable( {
  invitations
}: InvitationsTableProps ) {
  const [ inviteToDelete, setInviteToDelete ] = useState<OrgInvitation | null>( null );
  const [ showDeleteModal, setShowDeleteModal ] = useState<boolean>( false );

  const deleteInvitationMutation = useDeleteInvitationMutation();

  const handleDeleteClick = ( invitation: OrgInvitation ) => {
    setInviteToDelete( invitation );
    setShowDeleteModal( true );
  };

  const handleDeleteConfirm = () => {
    if( !inviteToDelete ) return;

    deleteInvitationMutation.mutate(
      {
        invitationId: inviteToDelete.id
      },
      {
        onSuccess: () => {
          toast.success( 'Invitation deleted successfully' );
          setInviteToDelete( null );
          setShowDeleteModal( false );
        },
        onError: ( e ) => {
          console.error( e );
          toast.error( 'Failed to delete invitation' );
        }
      }
    );
  };

  const handleDeleteCancel = () => {
    setInviteToDelete( null );
    setShowDeleteModal( false );
  };

  const columnHelper = createColumnHelper<OrgInvitation>();

  const columns = useMemo( () => [
    columnHelper.accessor( 'invitee.email', {
      id: 'email',
      header: 'Email',
      cell: info => info.getValue(),
      size: 300,
      enableSorting: true
    } ),
    columnHelper.accessor( 'createdAt', {
      id: 'createdAt',
      header: 'Created',
      cell: info => new Date( info.getValue() ).toLocaleDateString(),
      size: 150,
      enableSorting: true
    } ),
    columnHelper.accessor( 'expiresAt', {
      id: 'expiresAt',
      header: 'Expires',
      cell: info => new Date( info.getValue() ).toLocaleDateString(),
      size: 150,
      enableSorting: true
    } ),
    columnHelper.display( {
      id: 'actions',
      header: 'Actions',
      cell: ( { row } ) => {
        const invitation = row.original;
        return (
          <button
            aria-label='Delete invitation'
            disabled={ deleteInvitationMutation.isPending && inviteToDelete?.id === invitation.id }
            onClick={ () => handleDeleteClick( invitation ) }
            style={ {
              background: 'none',
              border: 'none',
              cursor: deleteInvitationMutation.isPending && inviteToDelete?.id === invitation.id ? 'not-allowed' : 'pointer',
              color: '#dc2626',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              opacity: deleteInvitationMutation.isPending && inviteToDelete?.id === invitation.id ? 0.5 : 1
            } }
            title='Delete invitation'
            type='button'
          >
            <FiTrash2 />
          </button>
        );
      },
      size: 100
    } )
  ], [ deleteInvitationMutation.isPending, inviteToDelete ] );

  const sortedInvitations = useMemo( () => {
    return [ ...invitations ].sort( ( a, b ) => {
      return new Date( b.createdAt ).getTime() - new Date( a.createdAt ).getTime();
    } );
  }, [ invitations ] );

  const table = useReactTable( {
    data: sortedInvitations,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10
      }
    }
  } );

  return (
    <>
      <NativeTable table={ table } />
      {
        showDeleteModal && inviteToDelete && (
          <Modal>
            <div>
              <h2>Delete Invitation</h2>
              <div className={ clsx( 'layout__col', 'pad' ) }>
                <p>Are you sure you want to delete the invitation for:</p>
                <p style={ { fontWeight: 'bold', marginTop: '0.5rem' } }>
                  { inviteToDelete.invitee.email }
                </p>
                <p style={ { color: '#666', marginTop: '0.5rem', fontSize: '0.875rem' } }>
                  This action cannot be undone.
                </p>
              </div>
              <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
                <Button
                  variant='secondary'
                  size='sm'
                  disabled={ deleteInvitationMutation.isPending }
                  onClick={ handleDeleteCancel }
                >
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  size='sm'
                  disabled={ deleteInvitationMutation.isPending }
                  onClick={ handleDeleteConfirm }
                  style={ { backgroundColor: '#dc2626' } }
                >
                  { deleteInvitationMutation.isPending ? 'Deleting...' : 'Delete Invitation' }
                </Button>
              </div>
            </div>
          </Modal>
        )
      }
    </>
  );
}
